import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Switch from "@mui/material/Switch";
import { Button } from '../../../Component/CommonFormEle';
import {
    fetchPolicyGroupSettings,
    updatePolicyGroupSetting,
    updatePolicySettingLock
} from "js/Api/index";

function WorkerCheckInTab({ user, policy, settingsFrom }) {
    const [settings, setSettings] = useState([]);
    const [enableStaffCheckIn, setEnableStaffCheckIn] = useState(false);
    const [autoEnableCheckOut, setAutoEnableCheckOut] = useState(false);
    const [enableTapStaffCheckIn, setenableTapStaffCheckIn] = useState(false);

    // For tracking if check-out state needs to be updated when check-in is disabled
    const [previousCheckInState, setPreviousCheckInState] = useState(false);
    const [workerSettingCategoryGroups, setWorkerSettingCategoryGroups] = useState([]);
    const [selectedWorkerOption, setSelectedWorkerOption] = useState("allow");

    const isReadOnly = selectedWorkerOption !== "manage";

    useEffect(() => {
        fetchWorkerSettings();
    }, []);

    // Effect to handle when check-in is disabled
    useEffect(() => {
        // If check-in was previously enabled and is now disabled
        if (previousCheckInState && !enableStaffCheckIn) {
            // Auto set check-out to false when check-in is disabled
            setAutoEnableCheckOut(false);
        }
        // Update the previous state for next comparison
        setPreviousCheckInState(enableStaffCheckIn);
    }, [enableStaffCheckIn]);

    const fetchWorkerSettings = async () => {
        try {
            const response = await fetchPolicyGroupSettings(policy.id);

            if (response.success) {
                setSettings(response.data.polictSettings);
                setWorkerSettingCategoryGroups(response.data.policySettingCategoryGroups);
                updateWorkerState(response.data.polictSettings);
                updateSelectedWorkerOption(response.data.policySettingCategoryGroups);
            } else {
                console.error("Failed to fetch Worker Check-in settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching Worker Check-in settings:", error);
        }
    };

    const updateWorkerState = (workerSettings) => {
        workerSettings.forEach((setting) => {
            if (setting.friendlyName === "settings.worker_signin") {
                setEnableStaffCheckIn(setting.value === "true");
            }
            if (setting.friendlyName === "settings.worker_signout") {
                setAutoEnableCheckOut(setting.value === "true");
            }
            if (setting.friendlyName === "settings.worker_checkin_multiple_tap") {
                setenableTapStaffCheckIn(setting.value === "true");
            }
        });
    };

    const updateSelectedWorkerOption = (categoryGroups) => {
        const workerCategory = categoryGroups.find(
            (category) => category.categoryName === "Worker Check-In"
        );
        if (workerCategory) {
            setSelectedWorkerOption(workerCategory.locked ? "manage" : "allow");
        }
    };

    const handleSaveWorkerSettings = async () => {
        // Find the settings we need to update
        const checkInSetting = settings.find(setting =>
            setting.friendlyName === "settings.worker_signin"
        );

        const checkOutSetting = settings.find(setting =>
            setting.friendlyName === "settings.worker_signout"
        );

        const TabSetting = settings.find(setting =>
            setting.friendlyName === "settings.worker_checkin_multiple_tap"
        );

        // Prepare the settings to update
        const updatedSettings = [];

        // Always update the check-in setting
        if (checkInSetting) {
            updatedSettings.push({
                ...checkInSetting,
                value: enableStaffCheckIn.toString()
            });
        }

        // Always update check-out setting (will be false if check-in is false)
        if (checkOutSetting) {
            updatedSettings.push({
                ...checkOutSetting,
                value: autoEnableCheckOut.toString()
            });
        }

        if (TabSetting) {
            updatedSettings.push({
                ...TabSetting,
                value: enableTapStaffCheckIn.toString()
            });
        }

        try {
            let hasError = false;

            for (const setting of updatedSettings) {
                const response = await updatePolicyGroupSetting(policy.id, setting.id, setting.value);

                if (!response.success) {
                    console.error(`Failed to update setting ${setting.friendlyName}:`, response.error);
                    hasError = true;
                }
            }

            if (hasError) {
                toast.error("Failed to update Worker Check-in settings");
            } else {
                toast.success("Worker Check-in settings updated successfully!");
                fetchWorkerSettings();
            }
        } catch (error) {
            console.error("Error updating Worker Check-in settings:", error);
            toast.error("Failed to update Worker Check-in settings");
        }
    };

    const handleCategoryLockChange = async (locked) => {
        const category = workerSettingCategoryGroups.find(
            (category) => category.categoryName === "Worker Check-In"
        );

        if (!category) return;

        try {
            const response = await updatePolicySettingLock(policy.id, category.id, locked);

            if (response.success) {
                // Update local state
                category.locked = locked;
                setWorkerSettingCategoryGroups([...workerSettingCategoryGroups]);
                setSelectedWorkerOption(locked ? "manage" : "allow");
            } else {
                console.error("Failed to update category lock:", response.error);
                toast.error("Failed to update category lock");
            }
        } catch (error) {
            console.error("Error updating category lock:", error);
            toast.error("Failed to update category lock");
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h4 className="d-inline">Worker Check-in</h4>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-md-6">
                    Enable Staff Check-in button on CareWindow device
                </div>
                <div className="col-md-6">
                    <Switch
                        checked={enableStaffCheckIn}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setEnableStaffCheckIn(newValue);
                            // If turning off check-in, also turn off check-out
                            if (!newValue) {
                                setAutoEnableCheckOut(false);
                                setenableTapStaffCheckIn(false);
                            }
                        }}
                        color="primary"
                    />
                </div>
            </div>
            {enableStaffCheckIn &&
                <>
                    <div className="row mt-2">
                        <div className="col-md-6">
                            Also enable Check-out
                        </div>
                        <div className="col-md-6">
                            <Switch
                                checked={autoEnableCheckOut}
                                onChange={(e) => setAutoEnableCheckOut(e.target.checked)}
                                color="primary"
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-6">
                            Require multiple taps on Staff Check-in button (to discourage the CareWindow user from opening it)
                        </div>
                        <div className="col-md-6">
                            <Switch
                                checked={enableTapStaffCheckIn}
                                onChange={(e) => setenableTapStaffCheckIn(e.target.checked)}
                                color="primary"
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </>
            }
            <div className="row mt-3">
                <div className="col-md-12">
                    <Button onClick={handleSaveWorkerSettings} variant="black">
                        Apply
                    </Button>
                </div>
            </div>
        </>
    );
}

export default WorkerCheckInTab;